import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton, Checkbox, Tooltip,
  FormControlLabel, Divider, Alert, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { buildSignatureHTML, buildPlainText, loadSignatureContext } from '../../utils/signatureHtml';
import {
  bodyTextToHtml, fetchAttachment, buildEml, emlFilename, downloadEml,
} from '../../utils/offerMailEml';

// Hälsningsfrasen är det som blir fel när man kopierar ett gammalt mail, så den
// byggs alltid om från de valda kontakterna — den går aldrig att ärva av misstag.
function firstName(name = '') {
  return String(name).trim().split(/\s+/)[0] || '';
}

function joinNames(names) {
  const list = names.filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} och ${list[list.length - 1]}`;
}

// Offerterna namnges olika från gång till gång, men "enterprise" respektive
// "projekt" i filnamnet räcker för att formulera meningen om antalet rätt.
// Gissar den fel skrivs texten om direkt i förhandsgranskningen.
function classifyOffers(files) {
  let project = 0, enterprise = 0, other = 0;
  files.forEach(f => {
    const n = (f.name || '').toLowerCase();
    if (n.includes('enterprise')) enterprise += 1;
    else if (n.includes('projekt') || n.includes('project')) project += 1;
    else other += 1;
  });
  return { project, enterprise, other, total: files.length };
}

function offerCountSentence(files) {
  const { project, enterprise, other, total } = classifyOffers(files);
  if (total === 0) return 'Här kommer offerterna som utlovat.';

  const plural = n => (n === 1 ? 'offert' : 'offerter');
  if (project && enterprise) {
    return `Här kommer ${project} ${plural(project)} för projektnivån och ${enterprise} för enterprise (obegränsat antal projekt).`;
  }
  if (project)    return `Här kommer ${project} ${plural(project)} för projektnivån.`;
  if (enterprise) return `Här kommer ${enterprise} ${plural(enterprise)} för enterprise (obegränsat antal projekt).`;
  return `Här kommer ${other} ${plural(other)} som utlovat.`;
}

const ONPREM_SENTENCE = 'OnPrem-varianten kan vi ta om/när du har ett case för det.';
const DEFAULT_THANKS  = 'tack för bra diskussioner igår';
const DEFAULT_CLOSING = 'Har du några frågor är det bara att ringa mig!';

// Brödtexten slutar med avslutningsfrasen. Signaturen läggs på av Outlook i
// mailto-varianten och byggs in i .eml-filen när bilagorna följer med.
function composeBody({ names, thanks, offerSentence, includeOnPrem, closing }) {
  const hello   = names ? `Hej ${names}` : 'Hej';
  const opening = thanks.trim() ? `${hello} och ${thanks.trim()}.` : `${hello}!`;
  const middle  = [offerSentence, includeOnPrem ? ONPREM_SENTENCE : ''].filter(Boolean).join(' ');
  return [opening, '', middle, '', closing.trim()].join('\n');
}

// Samma ämnesradsformat som mailen skickas med idag: FLATBOX | KUNDNAMN - Offerter
function defaultSubject(productName, companyName) {
  const product  = (productName || '').trim().toUpperCase();
  const customer = (companyName || '').trim().toUpperCase();
  if (product && customer) return `${product} | ${customer} - Offerter`;
  if (customer)            return `${customer} - Offerter`;
  return 'Offerter';
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function OfferMailDialog({
  open, onClose, company, contacts = [], attachments = [], productName = '',
  currentUser, onOpened, defaultContactId = '',
}) {
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [selectedFileIds,    setSelectedFileIds]    = useState([]);
  const [thanks,        setThanks]        = useState(DEFAULT_THANKS);
  const [includeOnPrem, setIncludeOnPrem] = useState(true);
  const [closing,       setClosing]       = useState(DEFAULT_CLOSING);
  const [subject,       setSubject]       = useState('');
  const [body,          setBody]          = useState('');
  const [bodyEdited,    setBodyEdited]    = useState(false);
  const [signature,     setSignature]     = useState({ html: '', text: '' });
  const [building,      setBuilding]      = useState(false);
  const [buildError,    setBuildError]    = useState('');

  const mailContacts = useMemo(() => contacts.filter(c => c.email), [contacts]);
  const offerFiles   = useMemo(() => attachments.filter(a => a.url), [attachments]);

  const selectedFiles = useMemo(
    () => offerFiles.filter(f => selectedFileIds.includes(f.id)),
    [offerFiles, selectedFileIds]
  );
  const recipients = useMemo(
    () => mailContacts.filter(c => selectedContactIds.includes(c.id)),
    [mailContacts, selectedContactIds]
  );

  // Förval vid öppning: leadets huvudkontakt, annars kundens primära (annars
  // första), och de bifogade filer vars namn ser ut som en offert.
  useEffect(() => {
    if (!open) return;
    const primary = mailContacts.find(c => c.id === defaultContactId)
      || mailContacts.find(c => c.primary) || mailContacts[0];
    setSelectedContactIds(primary ? [primary.id] : []);
    setSelectedFileIds(
      offerFiles.filter(f => /offert|quote|pris|fb\d/i.test(f.name || '')).map(f => f.id)
    );
    setThanks(DEFAULT_THANKS);
    setIncludeOnPrem(true);
    setClosing(DEFAULT_CLOSING);
    setSubject(defaultSubject(productName, company?.name));
    setBodyEdited(false);
    setBuildError('');
  }, [open, mailContacts, offerFiles, productName, company, defaultContactId]);

  // Outlook lägger inte på någon signatur i en .eml-fil, så den hämtas från
  // samma källa som signatursidan och byggs in i mailet.
  useEffect(() => {
    if (!open || !currentUser) return;
    let cancelled = false;
    (async () => {
      try {
        const { company: sigCompany, dept, user } = await loadSignatureContext(currentUser);
        if (cancelled) return;
        setSignature({
          html: buildSignatureHTML(sigCompany, dept, user, 'desktop', 'landscape'),
          text: buildPlainText(sigCompany, dept, user),
        });
      } catch {
        if (!cancelled) setSignature({ html: '', text: '' });
      }
    })();
    return () => { cancelled = true; };
  }, [open, currentUser]);

  const templateBody = useMemo(() => composeBody({
    names: joinNames(recipients.map(c => firstName(c.name))),
    thanks,
    offerSentence: offerCountSentence(selectedFiles),
    includeOnPrem,
    closing,
  }), [recipients, thanks, selectedFiles, includeOnPrem, closing]);

  // Så länge texten inte är handredigerad följer den valen till vänster.
  useEffect(() => {
    if (!bodyEdited) setBody(templateBody);
  }, [templateBody, bodyEdited]);

  const toggle = useCallback((setter, id) => {
    setter(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }, []);

  const mailtoHref = useMemo(() => {
    const to = recipients.map(c => c.email).join(',');
    const params = new URLSearchParams({ subject, body });
    // URLSearchParams kodar mellanslag som "+", vilket Outlook visar bokstavligt.
    return `mailto:${to}?${params.toString().replace(/\+/g, '%20')}`;
  }, [recipients, subject, body]);

  function handleOpenInOutlook() {
    window.location.href = mailtoHref;
    onOpened?.({ recipients, files: selectedFiles, subject, body, via: 'mailto' });
    onClose();
  }

  // Hämtar de ikryssade filerna och packar ihop dem med brödtext och signatur
  // till en .eml-fil. Öppnas den i Outlook ligger bilagorna redan på plats.
  async function handleCreateEml() {
    setBuilding(true);
    setBuildError('');
    try {
      const fetched = await Promise.all(selectedFiles.map(fetchAttachment));
      const eml = buildEml({
        to: recipients.map(c => ({ name: c.name, email: c.email })),
        subject,
        bodyText: signature.text ? `${body}\n\n${signature.text}` : body,
        bodyHtml: bodyTextToHtml(body) + (signature.html || ''),
        attachments: fetched,
      });
      const today = new Date().toISOString().split('T')[0];
      downloadEml(eml, emlFilename(company?.name, today));
      onOpened?.({ recipients, files: selectedFiles, subject, body, via: 'eml' });
      onClose();
    } catch (err) {
      setBuildError(
        `Kunde inte hämta bilagorna (${err.message}). Använd "Öppna i Outlook" och bifoga filerna manuellt.`
      );
    } finally {
      setBuilding(false);
    }
  }

  const tooLong = mailtoHref.length > 1900;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '88vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={800} sx={{ fontSize: '1.05rem' }}>Offertmail</Typography>
          <Typography variant="caption" color="text.secondary">
            Laddas ner som mailfil och öppnas som utkast i Outlook
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', p: 0, overflow: 'hidden' }}>

        {/* ── VÄNSTER: valen som styr texten ── */}
        <Box sx={{ width: 380, flexShrink: 0, p: 2.5, overflowY: 'auto',
          borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem' }}>
              Mottagare
            </Typography>
            {mailContacts.length === 0 ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Ingen kontakt med e-postadress finns på {company?.name || 'kunden'}.
              </Alert>
            ) : (
              <Box sx={{ mt: 0.5 }}>
                {mailContacts.map(c => (
                  <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox size="small" checked={selectedContactIds.includes(c.id)}
                      onChange={() => toggle(setSelectedContactIds, c.id)}
                      sx={{ color: '#FF6B00', '&.Mui-checked': { color: '#FF6B00' } }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {c.name}{c.primary ? ' ★' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap
                        sx={{ display: 'block' }}>
                        {c.email}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem' }}>
              Offerter att bifoga ({selectedFiles.length} valda)
            </Typography>
            {offerFiles.length === 0 ? (
              <Typography variant="caption" color="text.secondary"
                sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}>
                Inga handlingar på leadet ännu — ladda upp offerterna under
                Bifogade handlingar så räknas de med i texten.
              </Typography>
            ) : (
              <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {offerFiles.map(f => (
                  <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox size="small" checked={selectedFileIds.includes(f.id)}
                      onChange={() => toggle(setSelectedFileIds, f.id)}
                      sx={{ color: '#FF6B00', '&.Mui-checked': { color: '#FF6B00' } }} />
                    <InsertDriveFileIcon sx={{ fontSize: 16, color: '#C62828', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={f.name}>{f.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatBytes(f.size)}
                      </Typography>
                    </Box>
                    <Tooltip title="Ladda ner så du kan bifoga den">
                      <IconButton size="small" component="a" href={f.url}
                        target="_blank" rel="noopener noreferrer">
                        <DownloadIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Divider />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem' }}>
              Textbyggstenar
            </Typography>
            <TextField size="small" fullWidth label="Efter hälsningen" value={thanks}
              onChange={e => setThanks(e.target.value)}
              helperText={'Blir "Hej Mikael och tack för…". Töm för bara "Hej Mikael!"'}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }} />
            <FormControlLabel
              control={<Checkbox size="small" checked={includeOnPrem}
                onChange={e => setIncludeOnPrem(e.target.checked)}
                sx={{ color: '#FF6B00', '&.Mui-checked': { color: '#FF6B00' } }} />}
              label={<Typography variant="caption">Nämn OnPrem-varianten</Typography>} />
            <TextField size="small" fullWidth label="Avslutning" value={closing}
              onChange={e => setClosing(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }} />
          </Box>
        </Box>

        {/* ── HÖGER: redigerbar förhandsgranskning ── */}
        <Box sx={{ flex: 1, bgcolor: '#f5f5f5', p: 2.5, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem', flex: 1 }}>
              Förhandsgranskning — texten går att ändra direkt
            </Typography>
            {bodyEdited && (
              <Button size="small" startIcon={<RestartAltIcon sx={{ fontSize: 15 }} />}
                onClick={() => { setBodyEdited(false); setBody(templateBody); }}
                sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#FF6B00' }}>
                Återställ mallen
              </Button>
            )}
          </Box>

          <Box sx={{ bgcolor: '#fff', borderRadius: '10px', p: 2.5,
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
              <Typography variant="caption" sx={{ color: '#999', width: 52, flexShrink: 0 }}>
                Till
              </Typography>
              <Typography variant="caption" sx={{ color: '#222', fontWeight: 600 }}>
                {recipients.map(c => `${c.name} <${c.email}>`).join(', ') || '(välj mottagare)'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#999', width: 52, flexShrink: 0 }}>
                Ämne
              </Typography>
              <TextField variant="standard" fullWidth value={subject}
                onChange={e => setSubject(e.target.value)}
                InputProps={{ disableUnderline: true,
                  sx: { fontWeight: 700, fontSize: '0.9rem' } }} />
            </Box>
            <Divider sx={{ mb: 2 }} />

            <TextField multiline fullWidth variant="standard" minRows={10}
              value={body}
              onChange={e => { setBody(e.target.value); setBodyEdited(true); }}
              InputProps={{ disableUnderline: true,
                sx: { fontSize: '0.92rem', lineHeight: 1.75, color: '#222' } }} />

            <Box sx={{ mt: 1, pt: 2, borderTop: '1px dashed #ddd' }}>
              {signature.html ? (
                <Box sx={{ '& img': { maxWidth: '100%' } }}
                  dangerouslySetInnerHTML={{ __html: signature.html }} />
              ) : (
                <Typography variant="caption" sx={{ color: '#bbb', fontStyle: 'italic' }}>
                  Signaturen hämtas från Marknad → Mailsignatur.
                </Typography>
              )}
            </Box>

            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #eee' }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#2E7D32' }}>
                  {selectedFiles.length} {selectedFiles.length === 1 ? 'bilaga' : 'bilagor'} följer med mailet:
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {selectedFiles.map(f => (
                    <Typography key={f.id} variant="caption"
                      sx={{ display: 'block', color: '#666' }}>
                      {f.name}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {buildError && (
            <Alert severity="error" sx={{ mt: 2 }}>{buildError}</Alert>
          )}

          {tooLong && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Texten är lång — vissa mailprogram kapar långa mailto-länkar.
              Kontrollera att hela texten kom med i Outlook.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Avbryt
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          Loggas som "Offert skickad" och sätter offertstatus Skickad
        </Typography>
        <Tooltip title="Öppnar ett tomt mail i Outlook med din egen signatur — bilagorna får du dra in själv">
          <span>
            <Button variant="outlined" size="large" startIcon={<SendIcon />}
              disabled={recipients.length === 0 || !subject.trim() || !body.trim() || building}
              onClick={handleOpenInOutlook}
              sx={{ textTransform: 'none', borderColor: '#bbb', color: '#555' }}>
              Utan bilagor
            </Button>
          </span>
        </Tooltip>
        <Button variant="contained" size="large"
          startIcon={building
            ? <CircularProgress size={16} color="inherit" />
            : <AttachEmailIcon />}
          disabled={recipients.length === 0 || !subject.trim() || !body.trim() || building}
          onClick={handleCreateEml}
          sx={{ textTransform: 'none', fontWeight: 700,
            bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
          {building
            ? 'Hämtar bilagor…'
            : `Skapa mail med ${selectedFiles.length} ${selectedFiles.length === 1 ? 'bilaga' : 'bilagor'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
